import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalBody, ModalFooter } from 'reactstrap';
import {
  DATE_SHORT_FORMAT,
  MoneyFormatter,
  REPORTSERVER_BASEURL,
  fabmod_view_price_information_all,
  fabmod_send_po_to_nolans_all,
} from '../../constants';
import injectSheet from 'react-jss';
import { apiDownloadReport, apiPost, apiRequest } from '../../utils/request';
import { toastr } from 'react-redux-toastr';
import TextViewControl from '../common/TextViewControl';
import jQuery from 'jquery';
import moment from 'moment';
import { Base64 } from 'js-base64';
import { connect } from 'react-redux';
import { InvoiceTableStyles } from '../orders/OrderInvoiceSheet';

const defaultSheetData = {
  lines: [],
  total: 0,
  totalIncludingGst: 0,
  gstPercent: 10,
  unmappedWarnings: [],
  organisationBusinessAddress: '',
  organisationGroupShortName: '',
  canEmailOrganisationGroup: false,
  orderDate: null,
  requestedDeliveryDate: null,
};

// The server returns every figure at full precision; rounding to 2dp happens
// here, at the point of display, in both the on-screen sheet and the CSV.
// Round away from float noise (e.g. 1234.56 * 1.1 = 1358.0160000000001).
const round = (value, dp) => {
  let factor = Math.pow(10, dp);
  return Math.round((value + Number.EPSILON) * factor) / factor;
};

// Money as a plain 2dp number for the CSV - no currency symbol or thousands
// separator, so the cells stay numeric when opened in a spreadsheet.
const csvMoney = (value) => round(value, 2).toFixed(2);

// FileReader has no promise API; wrap it so the CSV and the PDF can both be
// read as data URLs without nesting callbacks.
const readAsDataUrl = (blob) => new Promise((resolve) => {
  let reader = new FileReader();
  reader.addEventListener('load', () => resolve(reader.result), false);
  reader.readAsDataURL(blob);
});

class NolanOrderSheet extends Component {
  constructor(props, context) {
    super(props, context);

    this.state = {
      order: props.order,
      sheetData: defaultSheetData,
      printed: false,
      emailing: false,
    };
    this.handlePrint = this.handlePrint.bind(this);
    this.handleDownloadCsv = this.handleDownloadCsv.bind(this);
    this.handleEmailGroup = this.handleEmailGroup.bind(this);
    this.handleQuantityChange = this.handleQuantityChange.bind(this);
    this.saveQuantities = this.saveQuantities.bind(this);
    this.handleResetQuantities = this.handleResetQuantities.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.buildCsv = this.buildCsv.bind(this);
    this.buildPdfRequestBody = this.buildPdfRequestBody.bind(this);
    this.csvFileName = this.csvFileName.bind(this);
    this.pdfFileName = this.pdfFileName.bind(this);
    this.loadSheetData = this.loadSheetData.bind(this);
    this.printSection = React.createRef();
  }

  csvFileName() {
    return `Order_${this.props.order.id}_nolan-sheet.csv`;
  }

  pdfFileName() {
    return `Order_${this.props.order.id}_nolan-sheet.pdf`;
  }

  // Shared by Download CSV and Email so the emailed file is byte-identical
  // to the downloaded one.
  buildCsv() {
    let { order } = this.props;
    let { sheetData } = this.state;

    let escape = (v) => {
      if (v === null || v === undefined) return '';
      let s = String(v);
      if (/[",\n\r]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    let headers = ['Product Group', 'Code', 'Description', 'Quantity', 'Unit', 'Cost', 'Line Total'];
    let rows = sheetData.lines.map(x => [
      x.productGroup || '',
      x.code || '',
      x.description || '',
      x.quantity > 0 ? x.quantity : '',
      x.unitOfMeasure || '',
      x.unitPrice > 0 ? csvMoney(x.unitPrice) : '',
      x.lineTotal > 0 ? csvMoney(x.lineTotal) : 'TBC',
    ].map(escape).join(','));
    let headerLines = [
      ['Customer Name', order.organisationDescription],
      // Plain CRN: this sheet goes to Nolans, which shouldn't see which distributor raised it.
      ['CRN', order.customerReference],
      // sheetData's group is where FABMOD orders go (always Nolans), not the order's own group.
      ...(order.groupPurchaseOrder ? [[`${sheetData.organisationGroupShortName} PO`, order.groupPurchaseOrder]] : []),
      ['Address', sheetData.organisationBusinessAddress],
      ['Order Date', sheetData.orderDate ? moment(sheetData.orderDate).format(DATE_SHORT_FORMAT) : ''],
      ...(sheetData.requestedDeliveryDate
        ? [['Requested Delivery Date', moment(sheetData.requestedDeliveryDate).format(DATE_SHORT_FORMAT)]]
        : []),
    ].map(l => l.map(escape).join(','));

    // Totals sit under the Line Total column so they line up with the rows above.
    let totalLines = [
      ['', '', '', '', '', 'Total (excluding GST)', csvMoney(sheetData.total)],
      ['', '', '', '', '', `Total (including GST ${sheetData.gstPercent}%)`, csvMoney(sheetData.totalIncludingGst)],
    ].map(l => l.map(escape).join(','));

    return headerLines.join('\r\n') + '\r\n\r\n'
      + [headers.join(','), ...rows, ...totalLines].join('\r\n');
  }

  handleDownloadCsv() {
    this.saveQuantities();
    let blob = new Blob([this.buildCsv()], { type: 'text/csv;charset=utf-8;' });
    let url = URL.createObjectURL(blob);
    let link = document.createElement('a');
    link.href = url;
    link.download = this.csvFileName();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  handleEmailGroup() {
    let { order } = this.props;
    let { sheetData, emailing } = this.state;

    if (emailing) return;

    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Email this Nolan order sheet to ${sheetData.organisationGroupShortName}?`)) {
      return;
    }

    this.saveQuantities();

    // No charset in the mime type - Helpers.GetFileContent can't parse
    // "data:text/csv;charset=utf-8;base64,..." and the send would fail.
    let csvBlob = new Blob([this.buildCsv()], { type: 'text/csv' });
    let _this = this;
    let posting = false;

    this.setState({ emailing: true });

    // Render the PDF the same way Print does, then post it alongside the CSV.
    apiDownloadReport(`${REPORTSERVER_BASEURL}/reports/generatepdf`,
      this.buildPdfRequestBody(), this.pdfFileName(), pdfBlob => {

        posting = true;
        toastr.info('Sending email...');

        Promise.all([readAsDataUrl(csvBlob), readAsDataUrl(pdfBlob)])
          .then(([csvContent, pdfContent]) => apiPost(`/api/fabrications/${order.id}/email/nolansheet`, {
            content: csvContent,
            fileName: _this.csvFileName(),
            pdfContent: pdfContent,
            pdfFileName: _this.pdfFileName(),
          }))
          .then(x => {
            _this.setState({ emailing: false });
            // performFetch already raises its own toastr on failure, so only
            // report success here rather than double-toasting the error.
            if (x && !x.error) {
              toastr.success('Email sent');
            }
          });
      }).then(() => {
        // performDownloadBlob swallows its errors after toasting them, so the
        // callback simply never fires. Release the button rather than leaving
        // it disabled for the life of the modal.
        if (!posting) {
          _this.setState({ emailing: false });
        }
      });
  }

  // Recomputes the edited line's total and the sheet-wide totals so Print, the CSV
  // and Email all reflect the edit immediately, without a round-trip to the server.
  handleQuantityChange(index, rawValue) {
    let { sheetData } = this.state;
    let qty = rawValue === '' ? 0 : parseFloat(rawValue);
    if (isNaN(qty) || qty < 0) return;

    let lines = sheetData.lines.map((l, i) => i === index
      ? { ...l, quantity: qty, lineTotal: l.unitPrice * qty }
      : l);
    let total = lines.reduce((sum, l) => sum + (l.lineTotal || 0), 0);
    let totalIncludingGst = total * (1 + (sheetData.gstPercent || 0) / 100);

    this.setState({ sheetData: { ...sheetData, lines, total, totalIncludingGst } });
  }

  // Posts the complete current set of quantities (not a diff) -- the server
  // replaces its stored overrides wholesale to match what's on screen. Silent
  // (no toast) since it's called implicitly from Print/Download/Email/Close
  // rather than as its own user-facing action -- there's no separate "Save
  // Quantities" button any more, editing the sheet and doing anything with it
  // saves the edit. A no-op while the sheet hasn't loaded any lines yet, so
  // closing the modal before data arrives can't wipe out real overrides.
  saveQuantities() {
    let { order } = this.props;
    let { sheetData } = this.state;

    if (sheetData.lines.length === 0) {
      return Promise.resolve();
    }

    let quantities = {};
    sheetData.lines.forEach(l => {
      if (l.code) quantities[l.code] = l.quantity || 0;
    });

    return apiPost(`/api/fabrications/${order.id}/nolansheet/quantities`, { quantities });
  }

  // Clears the stored overrides and reloads, so the sheet falls back to the
  // server-computed defaults (bracket packs, splines, etc.) -- the "fresh
  // slate" a user reaches for after editing quantities they didn't mean to.
  handleResetQuantities() {
    let { order } = this.props;

    apiPost(`/api/fabrications/${order.id}/nolansheet/quantities`, { quantities: {} })
      .then(x => {
        if (x && !x.error) {
          toastr.success('Quantities reset to defaults');
          this.loadSheetData();
        }
      });
  }

  handleClose() {
    this.saveQuantities();
    this.props.toggleOpen();
  }

  // The report-server payload for the printed sheet. Shared by Print and
  // Email so the emailed PDF is the same document the user would print.
  buildPdfRequestBody() {

    let container = jQuery(`<div></div>`);

    jQuery(this.printSection.current).clone().appendTo(container);

    container
      .css('font-size', '12px')
      .css('padding-right', '20px')
      .css('margin-top', '5px')
      .css('padding-top', '25px')
      .css('font-family', "'Open Sans', Arial, sans-serif")
      .css('line-height', "1.6em");

    container.find('h1').css('font-size', '40px');
    container.find('h2').css('font-size', '25px');
    container.find('h3').css('font-size', '16px');
    container.find('h4').css('font-size', '12px');
    container.find('h5').css('font-size', '11px');
    container.find('h6').css('font-size', '10px');
    container.find('.pi-row:first-child').css('margin-top', '0px');

    return JSON.stringify({
      "base64HtmlHead": Base64.encode(document.head.innerHTML),
      "base64HtmlBody": Base64.encode(container[0].innerHTML),
      "pdfOutput": true,
      "isLandscape": false,
      "templateName": "GeneratePdfDefault"
    });
  }

  handlePrint() {
    this.saveQuantities();

    apiDownloadReport(`${REPORTSERVER_BASEURL}/reports/generatepdf`,
      this.buildPdfRequestBody(), this.pdfFileName());

    if (this.props.print) {
      setTimeout(() => {
        this.setState({ printed: true });
        this.props.toggleOpen();
      }, 500);
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.isOpen) {
      this.loadSheetData();
    }
  }

  loadSheetData() {
    let { order } = this.props;
    this.setState({ sheetData: defaultSheetData });
    apiRequest(`/api/fabrications/${order.id}/nolansheet`)
      .then(x => {
        // Endpoint may not exist yet (task 004) — handle 404 / errors gracefully
        // by leaving the default empty state in place.
        if (!x || x.error || !x.data) {
          this.setState({ sheetData: defaultSheetData, printed: false });
          return;
        }
        // Accept either a top-level shape or a wrapping { sheet: ... } shape
        // (mirrors how OrderInvoiceSheet reads data.invoice).
        let data = x.data.sheet || x.data;
        this.setState({
          sheetData: {
            lines: Array.isArray(data.lines) ? data.lines : [],
            total: data.total || 0,
            totalIncludingGst: data.totalIncludingGst || 0,
            gstPercent: data.gstPercent || 0,
            unmappedWarnings: Array.isArray(data.unmappedWarnings) ? data.unmappedWarnings : [],
            organisationBusinessAddress: data.organisationBusinessAddress || '',
            organisationGroupShortName: data.organisationGroupShortName || '',
            canEmailOrganisationGroup: data.canEmailOrganisationGroup === true,
            orderDate: data.orderDate || null,
            requestedDeliveryDate: data.requestedDeliveryDate || null,
          },
          printed: false,
        });
      });
  }


  render() {
    let { classes, order, print, theme, user } = this.props;
    let { sheetData, printed } = this.state;

    if (!order) {
      return null;
    }

    // https://trello.com/c/ryRYuhV7/1009-fabmod-authorites -- "Production" users must not
    // see pricing or be able to send the order to Nolans; the server already strips pricing
    // from sheetData for them, this just keeps the UI from showing misleading blanks/zeroes.
    let canViewPrice = user.hasAccessTag(fabmod_view_price_information_all);
    let canSendToNolans = user.hasAccessTag(fabmod_send_po_to_nolans_all);

    if (print && !printed && sheetData.lines.length > 0) {
      setTimeout(this.handlePrint, 500);
    }

    return (
      <div>

        <Modal isOpen={this.props.isOpen}
               toggle={this.handleClose}
               style={{ minWidth: '940px' }}
        >
          <ModalBody>
            <div className="pi-section">

              <div className="pi-row">
                <div className="pi-col-md-12"
                     style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}
                >
                  <button onClick={this.handlePrint}
                          className="btn pi-btn-base pi-btn-icon-big"
                  >
                    <i className="fa fa-print"/>&nbsp;Print</button>
                  <button onClick={this.handleDownloadCsv}
                          className="btn pi-btn pi-btn-icon-big"
                          disabled={sheetData.lines.length === 0}
                  >
                    <i className="fa fa-download"/>&nbsp;Download CSV</button>
                  <button onClick={this.handleResetQuantities}
                          className="btn pi-btn pi-btn-icon-big"
                          disabled={sheetData.lines.length === 0}
                  >
                    <i className="fa fa-undo"/>&nbsp;Reset Quantities</button>
                  <button className="btn pi-btn pi-btn-icon-big"
                          onClick={this.handleClose}><i className="fa fa-times"/>&nbsp;Close</button>
                  <button onClick={this.handleEmailGroup}
                          className="btn btn-primary pi-btn-icon-big"
                          style={{ marginLeft: 'auto' }}
                          disabled={sheetData.lines.length === 0
                            || !sheetData.canEmailOrganisationGroup
                            || !canSendToNolans
                            || this.state.emailing}
                  >
                    <i className="fa fa-envelope"/>&nbsp;Place order</button>
                </div>
              </div>

              <div ref={this.printSection}>

                <div className="pi-row" style={{ marginBottom: '15px', marginTop: '25px' }}>
                  <div className="pi-col-sm-6">
                    <h4><img src={theme.data.images.logoFull} width={300} alt="Organisation Group Logo"/></h4>
                  </div>
                  <div className="pi-col-sm-6">
                    <div className={"pi-row"}>
                      <div className="pi-col-sm-12">
                        <div className={"pi-row"}>
                          <div className="pi-col-sm-6">
                            ABN: {theme.data.text.abn}
                          </div>
                          <div className="pi-col-sm-6" style={{ textAlign: 'right' }}>
                            Telephone: {theme.data.text.phone}
                          </div>
                        </div>
                        <div className={"pi-row"}>
                          <div className="pi-col-sm-6">
                            {theme.data.text.address1}
                          </div>
                          <div className="pi-col-sm-6" style={{ textAlign: 'right' }}>
                            Facsimilie: +61 2 9907 2219
                          </div>
                        </div>
                        <div className={"pi-row"}>
                          <div className="pi-col-sm-6">
                            {theme.data.text.address2}
                          </div>
                          <div className="pi-col-sm-6" style={{ textAlign: 'right' }}>
                            Email: {theme.data.text.email}
                          </div>
                        </div>
                        <div className={"pi-row"}>
                          <div className="pi-col-sm-6"/>
                          <div className="pi-col-sm-6" style={{ textAlign: 'right' }}>
                            {theme.data.text.dftLicense}
                          </div>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

                <div className="pi-row" style={{ marginBottom: '15px' }}>
                  <div className="pi-col-sm-6">
                    <TextViewControl labelText={'Ref'} value={`FM-1${String(order.id).padStart(5, '0')}`}/>
                    <TextViewControl labelText={'Customer'} value={order.organisationDescription}/>
                    <TextViewControl labelText={'CRN'} value={order.customerReference}/>
                    { order.groupPurchaseOrder &&
                        <TextViewControl labelText={`${sheetData.organisationGroupShortName} PO`} value={order.groupPurchaseOrder}/> }
                    <TextViewControl labelText={'Address'} value={order.organisationAddressComplete}/>
                    <TextViewControl labelText={'Email'} value={order.email1}/>
                    <TextViewControl labelText={'Phone'} value={order.phone1}/>
                    <TextViewControl labelText={'Order Date'}
                                     value={sheetData.orderDate ? moment(sheetData.orderDate).format(DATE_SHORT_FORMAT) : ''}/>
                    { sheetData.requestedDeliveryDate &&
                        <TextViewControl labelText={'Requested Delivery Date'}
                                         value={moment(sheetData.requestedDeliveryDate).format(DATE_SHORT_FORMAT)}/> }
                  </div>
                  <div className="pi-col-sm-6">
                    <h2 style={{ textAlign: 'right' }}>Nolan Order Sheet</h2>
                  </div>
                </div>

                <div className="pi-row">
                  <div className="pi-col-md-12">

                    {/*
                      Real <table> markup (not the InvoiceTableStyles div soup)
                      so the group-header rows can colSpan all data columns.
                      A div-based CSS-table can't represent colspan, which
                      breaks column-width inference and shoves the data into
                      the wrong cells when a single 100%-wide row coexists
                      with the 6-cell data rows.
                    */}
                    <table className={classes.divTable}
                           style={{ marginBottom: '0px', marginTop: '0px', tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: '15%' }}/>
                        <col style={{ width: '40%' }}/>
                        <col style={{ width: '10%' }}/>
                        <col style={{ width: '10%' }}/>
                        <col style={{ width: '12%' }}/>
                        <col style={{ width: '13%' }}/>
                      </colgroup>
                      <thead className={classes.divThead}>
                        <tr className={classes.divTrHead}>
                          <th className={classes.divTh}>Code</th>
                          <th className={classes.divTh}>Description</th>
                          <th className={classes.divTh} style={{ textAlign: 'right', paddingRight: '5px' }}>Quantity</th>
                          <th className={classes.divTh}>Unit</th>
                          <th className={classes.divTh} style={{ textAlign: 'right', paddingRight: '5px' }}>Cost</th>
                          <th className={classes.divTh} style={{ textAlign: 'right', paddingRight: '5px' }}>Line Total</th>
                        </tr>
                      </thead>
                      <tbody className={classes.divTbody}>
                        {(() => {
                          // Group lines by ProductGroup, preserving first-appearance order
                          // from the API response. Emit a header row at each group boundary.
                          let lastGroup = null;
                          let out = [];
                          sheetData.lines.forEach((x, i) => {
                            let rowStyle = x.isUnmapped ? { backgroundColor: '#fff3b0' } : undefined;
                            let descriptionPrefix = x.isUnmapped ? '⚠️ no Nolans mapping — ' : '';
                            let group = x.productGroup || '';
                            if (group !== lastGroup) {
                              lastGroup = group;
                              out.push(
                                <tr key={`group_${i}_${group}`} className={classes.divTr}>
                                  <td colSpan={6}
                                      className={classes.divTd}
                                      style={{ backgroundColor: '#e9ecef',
                                               fontWeight: 'bold',
                                               textAlign: 'left',
                                               padding: '4px 10px' }}>
                                    {group}
                                  </td>
                                </tr>
                              );
                            }
                            out.push(
                              <tr key={`${i}_${x.code || 'unmapped'}`} className={classes.divTr} style={rowStyle}>
                                <td className={classes.divTd}>{x.code}</td>
                                <td className={classes.divTd} style={{ textAlign: 'left' }}>{descriptionPrefix}{x.description}</td>
                                <td className={classes.divTdRight}>
                                  <input type="number"
                                         min="0"
                                         step="any"
                                         value={x.quantity || 0}
                                         onChange={(e) => this.handleQuantityChange(i, e.target.value)}
                                         style={{ width: '70px', textAlign: 'right' }}
                                  />
                                </td>
                                <td className={classes.divTd}>{x.unitOfMeasure}</td>
                                <td className={classes.divTdRight}>{canViewPrice && x.unitPrice > 0 ? MoneyFormatter.format(x.unitPrice) : ''}</td>
                                <td className={classes.divTdRight}>{canViewPrice ? (x.lineTotal > 0 ? MoneyFormatter.format(x.lineTotal) : 'TBC') : ''}</td>
                              </tr>
                            );
                          });
                          return out;
                        })()}

                        <tr className={classes.divTr}>
                          <td className={classes.divTd}>&nbsp;</td>
                          <td className={classes.divTd}>&nbsp;</td>
                          <td className={classes.divTdRight}>&nbsp;</td>
                          <td className={classes.divTd}>&nbsp;</td>
                          <td className={classes.divTdRight}>&nbsp;</td>
                          <td className={classes.divTdRight}>&nbsp;</td>
                        </tr>

                        {canViewPrice &&
                        <tr className={classes.divTr}>
                          <td className={classes.divTd} style={{ border: '0px' }}>&nbsp;</td>
                          <td className={classes.divTd} style={{ border: '0px' }}>&nbsp;</td>
                          <td colSpan={4}
                              className={classes.divTd}
                              style={{ fontWeight: 'bold', textAlign: 'right', paddingRight: '5px' }}>
                            Total (excluding GST):&nbsp;&nbsp;{MoneyFormatter.format(round(sheetData.total, 2))}
                          </td>
                        </tr>}

                        {canViewPrice &&
                        <tr className={classes.divTr}>
                          <td className={classes.divTd} style={{ border: '0px' }}>&nbsp;</td>
                          <td className={classes.divTd} style={{ border: '0px' }}>&nbsp;</td>
                          <td colSpan={4}
                              className={classes.divTd}
                              style={{ fontWeight: 'bold', textAlign: 'right', paddingRight: '5px' }}>
                            Total (including GST {sheetData.gstPercent}%):&nbsp;&nbsp;{MoneyFormatter.format(round(sheetData.totalIncludingGst, 2))}
                          </td>
                        </tr>}
                      </tbody>
                    </table>

                  </div>
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <button className="btn pi-btn pi-btn-icon-big"
                    onClick={this.handleClose}>
              <i className="fa fa-times"/>&nbsp;Close
            </button>
          </ModalFooter>
        </Modal>
      </div>
    );
  }
}

NolanOrderSheet.propTypes = {
  order: PropTypes.object.isRequired,
  isOpen: PropTypes.bool.isRequired,
  toggleOpen: PropTypes.func.isRequired,
  print: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
  theme: state.theme,
  user: state.user,
});

export default connect(mapStateToProps)(injectSheet(InvoiceTableStyles)(NolanOrderSheet));
